'use client';
import { shellOutcome } from '../../lib/toolPresentationCore';
import { plainContent } from '../../lib/plainContent';
import { useCallback, useRef, useState } from 'react';
import { Square, X, Users, Brain, Workflow, Cpu, SlidersHorizontal, PenLine, Folder, OctagonAlert, SquareSlash, SquareTerminal, MoonStar, ClipboardList, type LucideIcon } from 'lucide-react';
import { interpolate, plural, useTranslation } from '../../lib/i18n';
import type { LocaleDict } from '../../lib/i18n/types';
import type { ChatCommandOutcome, ChatEventDetails, ChatEventView, GoalStatusSnapshot } from '../../../src/shared/wireContract';
import { type SessionEventItem } from '../../lib/transcript';
import { localDateTime } from '../../lib/format';
import { Avatar } from '../../components/ui/Avatar';
import { LogColumn, LogGlyph, LogFold, ToolOutputBlock } from './ChatLog';
import { TraceLine, TraceTitle } from '../../components/ui/shadcn/trace';

/** A compact visible cwd label; the unchanged full identity remains available in the title. */
function shortCwd(detail: string): string {
  return detail.startsWith('/') ? `…/${detail.split('/').filter(Boolean).slice(-2).join('/')}` : detail;
}

/** The one lucide icon each event kind leads with, from the vocabulary the app already uses for the same
 *  idea: the telemetry rail's model chip and folder row, the reasoning button, the rename command, the stop
 *  button, the critical alert, the tool feed's shell and delegation icons. Distinct per kind, so a rename,
 *  a directory change and a finished sub-agent no longer share one gear. */
/** A reaction is folded onto the message it names (`web/lib/transcript.ts`) and is never a row. */
type RowDetails = Exclude<ChatEventView['details'], { kind: 'reaction' | 'membership' }>;

const EVENT_ICONS: Record<RowDetails['kind'], LucideIcon> = {
  model: Cpu, 'wire-api': Cpu, mode: SlidersHorizontal, reasoning: Brain, rename: PenLine, cwd: Folder,
  interrupted: Square, error: OctagonAlert, command: SquareSlash, shell: SquareTerminal,
  subagent: Users, workflow: Workflow, plan: X, 'plan-held': ClipboardList, dream: MoonStar,
};

/** What one event row says, worded in the reader's language: a quiet label and, for most kinds, a value
 *  joined by `→` (a changed setting, a command and its outcome) or `·` (a finish, a shell exit). A failed
 *  row takes the destructive tone on its icon and label only; the value stays ordinary text. */
interface EventRowView { label: string; sep?: '→' | '·'; value?: string; title?: string; failed?: boolean }

/** The model row's value: the model, the account that served it and — when the conversation moved to that
 *  account — why, in the row's own `·` chain. The daemon bounds the account line itself: a conversation is
 *  told about its OWN account, and a previous one its reader may no longer reach arrives WITHOUT a label, so
 *  this wording has nothing to name and no list to carry. A model row recorded before accounts existed
 *  simply has no account, and the row then reads exactly as it did. */
function modelEventValue(details: Extract<ChatEventDetails, { kind: 'model' }>, t: LocaleDict): string {
  const account = details.account;
  if (!account) return details.name ?? details.model;
  const moved = account.changed;
  const reason = moved ? t.brainChat.accountChangeReason[moved.reason] : null;
  return [
    details.name ?? details.model,
    account.label,
    ...(moved && reason
      ? [moved.fromLabel
        ? interpolate(t.brainChat.accountMovedFrom, { account: moved.fromLabel, reason })
        : interpolate(t.brainChat.accountMoved, { reason })]
      : []),
  ].join(' · ');
}

/** The row of every event kind. Mirror of the CLI's `chatEventLines`. */
function eventRowView(details: RowDetails, t: LocaleDict, locale: string): EventRowView {
  switch (details.kind) {
    case 'model': return { label: t.brainChat.eventModel, sep: '→', value: modelEventValue(details, t) };
    case 'wire-api': return { label: interpolate(t.brainChat.eventWireApi, { api: details.api }) };
    case 'reasoning': return { label: t.brainChat.eventReasoning, sep: '→', value: details.label };
    case 'mode': return { label: t.brainChat.eventMode, sep: '→', value: t.brainChat.workMode[details.mode] };
    case 'plan': return { label: t.brainChat.eventPlanDismissed };
    case 'plan-held': return { label: details.location.kind === 'server' ? t.brainChat.eventPlanHeldServer
      : interpolate(details.location.kind === 'computer' ? t.brainChat.eventPlanHeldComputer : t.brainChat.eventPlanHeldProject,
        { name: details.location.name }) };
    case 'rename': return { label: t.brainChat.eventRenamed, sep: '→', value: `"${details.title}"` };
    case 'cwd': return details.offlineTarget
      ? { label: interpolate(t.brainChat.eventOfflineTarget, { computer: details.offlineTarget.label, project: details.detail }) }
      : { label: t.brainChat.eventCwd, sep: '→', value: shortCwd(details.detail), title: details.detail };
    case 'subagent': {
      const failed = details.status === 'error';
      // The delegation's name is what the rail showed while it ran; the task line is the fallback.
      const name = details.name ?? details.task;
      return { label: failed ? t.brainChat.eventSubagentFailed : t.brainChat.eventSubagentDone, failed, ...(name ? { sep: '·', value: name } : {}) };
    }
    case 'workflow': {
      const label = details.status === 'error' ? t.brainChat.eventWorkflowFailed
        : details.status === 'cancelled' ? t.brainChat.eventWorkflowStopped : t.brainChat.eventWorkflowDone;
      const tally = `${details.ran}/${details.total} ${t.brainChat.eventNodes}`;
      return { label, sep: '·', value: details.title ? `${details.title} · ${tally}` : tally, failed: details.status === 'error' };
    }
    case 'interrupted': return {
      label: details.cause === 'step-limit'
        ? interpolate(t.brainChat.eventInterruptedStepLimit, { n: details.stepLimit ?? 0 })
        : details.cause === 'parent'
          ? t.brainChat.eventInterruptedParent
          : details.phase === 'tool' ? t.brainChat.eventInterruptedTool : t.brainChat.eventInterruptedUser,
    };
    case 'error': return { label: t.brainChat.eventError, value: errorEventLabel(details, t, locale), failed: true };
    case 'command': return {
      label: `/${details.name}${details.args ? ` ${details.args}` : ''}`, sep: '→',
      value: commandOutcomeLabel(details.outcome, t), failed: !details.outcome.ok,
    };
    // A run of automatic memory maintenance improved stored memories, so what this conversation recalls
    // from now on is not what it wrote earlier. The reader sees the count; the model reads the same
    // notice in its own English form.
    case 'dream': return { label: interpolate(plural(t.brainChat.eventDreaming, details.changed), { n: details.changed }) };
    case 'shell': {
      const outcome = shellOutcome(details);
      const killed = outcome.reason === 'timeout' ? t.brainChat.shellKilledTimeout
        : outcome.reason === 'overflow' ? t.brainChat.shellKilledOverflow
          : outcome.reason === 'abort' ? t.brainChat.shellKilledAbort : undefined;
      const status = killed ?? (details.exitCode !== null ? interpolate(t.brainChat.shellExit, { code: details.exitCode }) : undefined);
      return {
        label: `$ ${details.command}`, failed: outcome.failed,
        ...(status ? { sep: '·', value: status } : {}),
      };
    }
  }
}

/** A `/goal status` report worded in the reader's language; the goal, pause reason and evidence are
 *  the stored text itself. Mirror of the daemon's English `goalStatusText`. */
function goalStatusLabel(goal: GoalStatusSnapshot, t: LocaleDict): string {
  if (!goal) return t.brainChat.commandGoalNone;
  const parts = [
    interpolate(t.brainChat.commandGoalStatus, {
      status: t.brainChat.goalState[goal.status], used: goal.turnsUsed, budget: goal.turnBudget,
      turns: plural(t.telemetry.goalTurns, goal.turnBudget),
    }),
    goal.goal,
  ];
  if (goal.subgoals) parts.push(interpolate(t.brainChat.commandGoalSubgoals, goal.subgoals));
  if (goal.pausedReason) parts.push(interpolate(t.brainChat.commandGoalPausedReason, { reason: goal.pausedReason }));
  if (goal.evidence) parts.push(interpolate(t.brainChat.commandGoalEvidence, { evidence: goal.evidence }));
  return parts.join(' · ');
}

/** The text of a failure: the reader's wording when the daemon wrote it (it then carries a notice),
 *  otherwise the provider's or the thrown error's own text, verbatim. */
function errorEventLabel(details: Extract<ChatEventDetails, { kind: 'error' }>, t: LocaleDict, locale: string): string {
  const notice = details.notice;
  if (!notice) return details.text;
  switch (notice.code) {
    case 'reaction-refused': return t.chat.reactionFailed;
    case 'monthly_limit': return interpolate(t.spendLimit.monthlyLimit, { date: localDateTime(notice.resetsAt, locale) });
    // unpriced_model is stored history only; no code emits it anymore.
    case 'unpriced_model': return interpolate(t.spendLimit.unpricedModel, { date: localDateTime(notice.resetsAt, locale) });
    case 'account_spend_limit': return interpolate(t.spendLimit.accountLimit, { account: notice.account, date: localDateTime(notice.resetsAt, locale) });
    case 'provider-retries': return t.brainChat.eventErrorProviderRetries;
    case 'compaction-stopped': return interpolate(t.brainChat.eventErrorCompactionStopped, { n: notice.failures });
    case 'compaction-unreachable': return interpolate(t.brainChat.eventErrorCompactionUnreachable, { floor: notice.floorTokens, trigger: notice.triggerTokens });
    case 'native-checkpoint-conversion': return t.brainChat.nativeCheckpointConversionFailed;
    case 'model-switch-failed': return interpolate(t.brainChat.eventErrorModelSwitchFailed, {
      error: notice.reason === 'native-checkpoint-conversion' ? t.brainChat.nativeCheckpointConversionFailed : details.text,
    });
  }
}

/** The localized outcome of a command the user ran. A failure is worded from its notice when the daemon
 *  wrote the reason itself, and otherwise shows the exact text the daemon answered (a provider's or a
 *  library's own words have no translation). */
function commandOutcomeLabel(outcome: ChatCommandOutcome, t: LocaleDict): string {
  if (!outcome.ok) {
    switch (outcome.notice?.code) {
      case 'restart-unavailable': return t.brainChat.commandFailedRestartUnavailable;
      case 'not-running': return t.brainChat.commandFailedNotRunning;
      case undefined: return outcome.error;
    }
  }
  switch (outcome.code) {
    case 'compacted': return t.brainChat.commandCompacted;
    case 'not-compacted': return outcome.reason === 'already' ? t.brainChat.commandNotCompactedAlready : t.brainChat.commandNotCompactedNothing;
    case 'goal-set': return interpolate(outcome.draft ? t.brainChat.commandGoalDraft : t.brainChat.commandGoalSet, { goal: outcome.goal });
    case 'goal-paused': return t.brainChat.commandGoalPaused;
    case 'goal-resumed': return t.brainChat.commandGoalResumed;
    case 'goal-cleared': return t.brainChat.commandGoalCleared;
    case 'goal-status': return goalStatusLabel(outcome.goal, t);
    case 'fast':
      if (!outcome.available) return t.brainChat.commandFastUnavailable;
      return outcome.on ? t.brainChat.commandFastOn : t.brainChat.commandFastOff;
    case 'restart-requested': return t.brainChat.commandRestart;
  }
}

/** Whether any part of a one-line row is cut off by its ellipsis, measured on the row's line element and
 *  kept up to date as the column resizes. Sticky: once a row has had to truncate it stays unfoldable, so
 *  opening it (which un-truncates the text) never takes the fold away under the reader. The first check
 *  runs as the ref attaches, before paint, so a truncated row is never drawn without its chevron. */
function useTruncated(): [boolean, (node: HTMLElement | null) => void] {
  const [truncated, setTruncated] = useState(false);
  const observer = useRef<ResizeObserver | null>(null);
  const lineRef = useCallback((node: HTMLElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const check = (): void => {
      if ([...node.children].some((child) => child.scrollWidth > child.clientWidth + 1)) setTruncated(true);
    };
    check();
    observer.current = new ResizeObserver(check);
    observer.current.observe(node);
  }, []);
  return [truncated, lineRef];
}

/** A stored chat event, drawn from the same entry the model reads, as one quiet log row in the tool rows'
 *  column: the kind's icon, a label and a value on a single line. A row with more to say folds with the
 *  tool rows' disclosure: any row whose text the column had to truncate (a long failure, a goal report, a
 *  narrow phone) unfolds in place to its full wrapped text, because a phone has no hover to read the
 *  title, and a `!cmd` reveals its output in a bounded, scrollable console block. */
function ChatEventRow({ event }: { event: ChatEventView }) {
  const { t, locale } = useTranslation();
  const [truncated, lineRef] = useTruncated();
  const details = event.details;
  if (details.kind === 'reaction') throw new Error(`Reaction event ${event.id} reached the event rows; the transcript fold owns reactions`);
  if (details.kind === 'membership') {
    const template = details.action === 'joined' ? t.brainChat.eventMembershipJoined : details.action === 'left' ? t.brainChat.eventMembershipLeft
      : details.actor ? t.brainChat.eventMembershipRemoved : t.brainChat.eventMembershipRemovedAnonymous;
    return <div data-testid="chat-event-marker" data-kind="membership" className="flex items-start justify-center gap-2 text-meta text-muted-foreground">
      <Avatar user={{ ...details.subject, avatar: details.subject.avatar ?? undefined }} size={20} />
      <span className="min-w-0 text-center">{interpolate(template, { name: details.subject.name, actor: details.actor?.name ?? '' })} · {new Date(details.at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}</span>
    </div>;
  }
  const view = eventRowView(details, t, locale);
  const Icon = EVENT_ICONS[details.kind];
  // Whitespace text nodes between the spans are not laid out in the flex row, but they keep the row's
  // text readable when it is copied or read as a whole ("model → GPT-5.4").
  const head = (
    <>
      <LogGlyph><Icon aria-hidden className={`size-3 ${view.failed ? 'text-destructive' : ''}`} /></LogGlyph>
      {/* Beside a value the label keeps its words while the value gives way; only a label longer than
          most of the row (a long command line) truncates too. Alone, it has the whole row. */}
      <TraceTitle className={`${view.value !== undefined ? 'max-w-3/5 shrink-0' : ''} group-data-[state=open]/fold:whitespace-pre-wrap ${view.failed ? 'text-destructive' : ''}`}>{view.label}</TraceTitle>
      {view.value !== undefined ? (
        <>
          {' '}
          {view.sep ? <><span aria-hidden className="shrink-0">{view.sep}</span>{' '}</> : null}
          <span title={view.title ?? view.value} className="min-w-0 truncate text-foreground group-data-[state=open]/fold:whitespace-pre-wrap">{view.value}</span>
        </>
      ) : null}
    </>
  );
  const body = details.kind === 'shell' && details.output.trim() !== '' ? (
    // Indented to the label, past the icon slot and its gap. Bounded: a long listing scrolls inside its
    // own box instead of taking several screens of the transcript.
    <div className="pl-4.5">
      <div data-testid="chat-event-output" tabIndex={0} role="group" aria-label={t.brainChat.shellOutput} className="max-h-[40dvh] overflow-y-auto">
        <ToolOutputBlock output={{ title: 'shell', kind: 'console', text: plainContent(details.output) }} />
      </div>
    </div>
  ) : undefined;
  return (
    <div data-testid="chat-event-marker" data-kind={details.kind} role={details.kind === 'error' ? 'alert' : undefined}>
      {body || truncated
        ? <LogFold head={head} body={body} lineRef={lineRef} />
        : <TraceLine ref={lineRef} status={view.failed ? 'error' : undefined}>{head}</TraceLine>}
    </div>
  );
}

/** A run of event rows: stored chat events and delegated jobs' finishes — the machine annotating what
 *  happened, the web twin of the CLI's event rows. On the full page the run sits in the same turn gutter and
 *  log column as the tool rows, its rows flush with each other on one left edge; `breakBefore` opens the
 *  transcript's block gap above the group, and the turn after it opens the same gap below. */
/** A run of event rows in the shared log column, without a turn shell: what `SessionEvents` draws inside its
 *  turn and what an assistant run's work timeline draws for the events between two of its steps. */
export function EventRows({ events }: { events: SessionEventItem[] }) {
  const rows = [];
  for (let start = 0; start < events.length;) {
    const event = events[start]!;
    if (event.event.details.kind === 'membership') {
      rows.push(<div key={event.id} className={start > 0 ? 'pt-6' : undefined}><ChatEventRow event={event.event} /></div>);
      start += 1;
    } else {
      let end = start + 1;
      while (end < events.length && events[end]!.event.details.kind !== 'membership') end += 1;
      rows.push(<LogColumn key={event.id}>{events.slice(start, end).map(e => <ChatEventRow key={e.id} event={e.event} />)}</LogColumn>);
      start = end;
    }
  }
  return <>{rows}</>;
}

export function SessionEvents({ events, tk, breakBefore, first }: { events: SessionEventItem[]; tk?: string; breakBefore?: boolean; first?: boolean }) {
  const rows = <EventRows events={events} />;
  return (
    <div data-tk={tk} data-testid="chat-turn" data-role="event" data-first={first ? '' : undefined} data-continues={breakBefore ? undefined : ''} className="chat-turn grid grid-cols-[--spacing(4)_1fr] gap-x-3">
      <span aria-hidden className="chat-turn__mark-slot"><span className="chat-turn__marker" /></span>
      <div className="chat-turn__column min-w-0">{rows}</div>
    </div>
  );
}
